import React, { useState, useEffect, useRef } from 'react';
import { useFormContext, Controller, get, useWatch } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../../order.schema';
import { FormGrid, FormGroup, HintText, Input, Label, Select, Textarea } from '../../style';
import { SpacedFormGrid, CenteredFormGrid, WideFormGroup, StockWarning, PriceLockBox, PriceLockText } from './ItemGeneralFieldsSection.style';
import ConfirmationModal from '../../../../../../components/ModalConfirmation';
import { createItemInitialState } from '../../constants';
import ApiService from '../../../../../../services/ApiService';
import { useItemPricePendencies } from '../../Hooks/useItemPricePendencies';
import { useEspecificacaoAutoFill } from '../../Hooks/useEspecificacaoAutoFill';
import { hasDimensionsRule } from './ItemTypes/Product/FinalProductDimensionsSection';

interface ItemGeneralFieldsSectionProps {
  itemPath: ItemPath;
  /** Slot do select de Tipo de Item (vive no Items/index, que detém o handleItemTypeChange). */
  typeSelectSlot?: React.ReactNode;
  /** Bloco de identificação por tipo (ex.: Código/Produto/Script do PE), logo após a 1ª linha. */
  identificationSlot?: React.ReactNode;
  /** Bloco de dimensões por tipo (ex.: Dimensão/Comprimento do PE), logo antes da linha de quantidades. */
  dimensionsSlot?: React.ReactNode;
  /** Bloco de detalhes por tipo (ex.: Fabricante/Formato/Acabamento do PE), logo após a linha de quantidades. */
  detailsSlot?: React.ReactNode;
  /** Célula de anexos por tipo (ex.: Arquivo do PE), renderizada na última linha ao lado da Observação. */
  attachmentSlot?: React.ReactNode;
}

interface SelectOption {
  id: number;
  name: string;
}

const COMMERCIAL_UNITS = ['KG', 'UN', 'MT', 'M²', 'M³', 'CM', 'MM', 'Tn', 'Litros', 'Hora', 'Dia'];

const DEFAULT_UNIT_BY_ITEM_TYPE: Record<string, string> = { MATERIAL: 'KG', RESOURCE: 'UN' };

// Unidades em que a quantidade já é a contagem de peças (espelha o service do steel-orders).
const COUNTABLE_UNITS = ['UN', 'PC'];

// Path da quantidade dentro do bloco por tipo — a ponte cabeçalho → bloco mantém os
// consumidores existentes (validação de estoque, save flow, fórmula do serviço) funcionando.
const BLOCK_QTY_KEY: Record<string, string> = {
  MATERIAL: 'feedstock.quantidade',
  PRODUCT: 'finalProduct.quantidade',
  RESOURCE: 'resource.quantidade',
  SERVICE: 'stage.quantidade',
};

const ItemGeneralFieldsSection: React.FC<ItemGeneralFieldsSectionProps> = ({ itemPath, typeSelectSlot, identificationSlot, dimensionsSlot, detailsSlot, attachmentSlot }) => {
  const { control, register, setValue, getValues, formState: { errors } } = useFormContext<OrderSchemaType>();

  const [formats, setFormats] = useState<SelectOption[]>([]);
  const [finishings, setFinishings] = useState<SelectOption[]>([]);
  const [units, setUnits] = useState<SelectOption[]>([]);
  const [showClearUnitConfirm, setShowClearUnitConfirm] = useState(false);

  const unitId = useWatch({ control, name: `${itemPath}.unitOfMeasurementId` as any });
  const itemType = useWatch({ control, name: `${itemPath}.itemType` as any });
  const headerQuantity = useWatch({ control, name: `${itemPath}.quantity` as any });
  const unitFromProduct = useWatch({ control, name: `${itemPath}.unitFromProduct` as any }) as boolean | undefined;
  const formatIdValue = useWatch({ control, name: `${itemPath}.formatId` as any });
  const isTopLevelItem = !itemPath.includes('.subItems.');

  // Aviso de estoque à mostra enquanto o vendedor digita (a trava dura é a do save):
  // compara na unidade soberana do item via snapshot de disponibilidade do estoque.
  const stockQuantitiesWatch = useWatch({ control, name: `${itemPath}.feedstock.stockQuantities` as any }) as any[] | undefined;
  const stockAvailable = Array.isArray(stockQuantitiesWatch)
    ? stockQuantitiesWatch.find((q: any) => Number(q?.unitOfMeasurementId) === Number(unitId))
    : undefined;
  const stockExceeded = !!stockAvailable && stockAvailable.quantity != null
    && (Number(headerQuantity) || 0) > Number(stockAvailable.quantity);

  useEffect(() => {
    let active = true;
    const fetchOptions = async () => {
      try {
        const [formatsRes, finishingsRes, unitsRes] = await Promise.all([
          ApiService.stockLookup.getFormats(),
          ApiService.stockLookup.getFinishings(),
          // unidades vêm do catálogo global; o de service-stage devolve ids de outra base
          ApiService.resources.getUnits(),
        ]);
        if (!active) return;
        setFormats(Array.isArray(formatsRes.data) ? formatsRes.data : []);
        setFinishings((Array.isArray(finishingsRes.data) ? finishingsRes.data : [])
          .map((f: any) => ({ id: f.id, name: f.name || f.type || '' }))
          .filter((f: SelectOption) => f.name !== ''));
        setUnits(Array.isArray(unitsRes.data) ? unitsRes.data : []);
      } catch (e) {
        console.error('Erro ao buscar formatos/acabamentos/unidades:', e);
      }
    };
    fetchOptions();
    return () => { active = false; };
  }, []);

  // Sincroniza o nome da unidade (usado na dica de UX do filtro de serviços)
  // quando o item carregou só com o id (edição de pedido existente).
  useEffect(() => {
    if (!unitId || units.length === 0) return;
    const currentName = getValues(`${itemPath}.unitOfMeasurementName` as any) as any;
    if (!currentName) {
      const match = units.find(u => Number(u.id) === Number(unitId));
      if (match) {
        setValue(`${itemPath}.unitOfMeasurementName` as any, match.name, { shouldDirty: false, shouldValidate: false });
      }
    }
  }, [unitId, units, itemPath, getValues, setValue]);

  const legacySeedDoneRef = useRef(false);
  useEffect(() => {
    const blockKey = BLOCK_QTY_KEY[String(itemType)];
    if (!blockKey) return;
    const blockPath = `${itemPath}.${blockKey}` as any;

    // Hidratação de pedido existente: bloco vem preenchido e o cabeçalho vazio — semeia uma vez.
    if (!legacySeedDoneRef.current) {
      legacySeedDoneRef.current = true;
      const blockValue = getValues(blockPath) as any;
      if ((headerQuantity === undefined || headerQuantity === null || headerQuantity === '') && blockValue) {
        setValue(`${itemPath}.quantity` as any, Number(blockValue) || undefined, { shouldDirty: false, shouldValidate: false });
        return;
      }
    }

    const current = getValues(blockPath) as any;
    const next = headerQuantity === undefined || headerQuantity === null || headerQuantity === '' ? '' : String(headerQuantity);
    if (String(current ?? '') !== next) {
      setValue(blockPath, next, { shouldDirty: true, shouldValidate: false });
    }
  }, [headerQuantity, itemType, itemPath, getValues, setValue]);

  const hasComposition = () => {
    const v = getValues(itemPath) as any;
    return (v?.subItems?.length ?? 0) > 0
      || !!v?.feedstock?.materiaPrima
      || !!v?.finalProduct?.produto
      || !!v?.resource?.insumo
      || !!v?.stage?.servico;
  };

  const unitSeededTypeRef = useRef<string | null>(null);
  useEffect(() => {
    if (units.length === 0) return;
    if (unitSeededTypeRef.current === String(itemType)) return;
    unitSeededTypeRef.current = String(itemType);
    const defaultUnitName = DEFAULT_UNIT_BY_ITEM_TYPE[String(itemType)];
    if (!defaultUnitName) return;
    if (getValues(`${itemPath}.unitOfMeasurementId` as any)) return;
    const v = getValues(itemPath) as any;
    const hasComp = (v?.subItems?.length ?? 0) > 0 || !!v?.feedstock?.materiaPrima
      || !!v?.finalProduct?.produto || !!v?.resource?.insumo || !!v?.stage?.servico;
    if (hasComp) return;
    const unit = units.find(u => u.name.toUpperCase() === defaultUnitName);
    if (unit) {
      setValue(`${itemPath}.unitOfMeasurementId` as any, unit.id, { shouldDirty: true, shouldValidate: false });
      setValue(`${itemPath}.unitOfMeasurementName` as any, unit.name, { shouldDirty: true, shouldValidate: false });
    }
  }, [units, itemType, itemPath, getValues, setValue]);

  const handleUnitChange = (rawValue: string, fieldOnChange: (value: any) => void) => {
    if (!rawValue && hasComposition()) {
      setShowClearUnitConfirm(true); // mantém a unidade atual até a confirmação
      return;
    }
    const newUnitId = rawValue ? Number(rawValue) : undefined;
    fieldOnChange(newUnitId);
    const unitName = newUnitId !== undefined ? (units.find(u => Number(u.id) === newUnitId)?.name || '') : '';
    setValue(`${itemPath}.unitOfMeasurementName` as any, unitName, { shouldDirty: true, shouldValidate: false });
  };

  const confirmClearUnit = () => {
    const type = String(getValues(`${itemPath}.itemType` as any) || 'MATERIAL') as 'MATERIAL' | 'PRODUCT' | 'RESOURCE' | 'SERVICE';
    const init = createItemInitialState(type) as any;
    const opts = { shouldDirty: true, shouldValidate: false } as const;
    setValue(`${itemPath}.unitOfMeasurementId` as any, undefined, opts);
    setValue(`${itemPath}.unitOfMeasurementName` as any, '', opts);
    setValue(`${itemPath}.subItems` as any, [], opts);
    if (type === 'MATERIAL') {
      setValue(`${itemPath}.feedstock` as any, init.feedstock, opts);
      setValue(`${itemPath}.offeredProductMaterial` as any, init.offeredProductMaterial, opts);
      setValue(`${itemPath}.requestedProductMaterial` as any, init.requestedProductMaterial, opts);
    }
    if (type === 'PRODUCT') setValue(`${itemPath}.finalProduct` as any, init.finalProduct, opts);
    if (type === 'RESOURCE') setValue(`${itemPath}.resource` as any, init.resource, opts);
    if (type === 'SERVICE') setValue(`${itemPath}.stage` as any, init.stage, opts);
    setValue(`${itemPath}.priceDetails` as any, { ...init.priceDetails, sellingPrice: undefined }, opts);
    setValue(`${itemPath}.itemPrice` as any, undefined, opts);
    setValue(`${itemPath}.cost` as any, undefined, opts);
    setValue(`${itemPath}.markup` as any, undefined, opts);
    setShowClearUnitConfirm(false);
  };

  const commercialUnits = units.filter(u => COMMERCIAL_UNITS.includes(u.name));

  const selectedUnitName = units.find(u => Number(u.id) === Number(unitId))?.name || '';
  const isKgUnit = selectedUnitName.toUpperCase() === 'KG';
  const isCountableUnit = COUNTABLE_UNITS.includes(selectedUnitName.toUpperCase());
  const isServiceType = String(itemType) === 'SERVICE';
  const isProductType = String(itemType) === 'PRODUCT';

  // Produto raiz com formato reconhecido: Peso e Quantidade de peças vivem no bloco de dimensões,
  // com o Peso derivado da massa (useWeightAutoFill). Nos demais itens seguem aqui, e em KG o
  // Peso espelha a quantidade.
  const formatName = formats.find(f => Number(f.id) === Number(formatIdValue))?.name;
  const weightOwnedByDimensions = isProductType && isTopLevelItem && hasDimensionsRule(formatName);

  useEffect(() => {
    if (!isKgUnit || weightOwnedByDimensions) return;
    const qty = Number(headerQuantity);
    const next = !isNaN(qty) && qty > 0 ? qty : undefined;
    const current = getValues(`${itemPath}.weight` as any) as any;
    if (Number(current) !== next && !(current == null && next === undefined)) {
      setValue(`${itemPath}.weight` as any, next, { shouldDirty: true, shouldValidate: false });
    }
  }, [isKgUnit, weightOwnedByDimensions, headerQuantity, itemPath, getValues, setValue]);

  // Unidade contável: a quantidade já é a contagem de peças. Sem isso o item ia para o
  // faturamento com peças vazio e o fiscal o recusava por "sem peças".
  useEffect(() => {
    if (!isCountableUnit) return;
    const qty = Number(headerQuantity);
    const next = !isNaN(qty) && qty > 0 ? Math.floor(qty) : undefined;
    const current = getValues(`${itemPath}.quantityPieces` as any) as any;
    if (Number(current) !== next && !(current == null && next === undefined)) {
      setValue(`${itemPath}.quantityPieces` as any, next, { shouldDirty: true, shouldValidate: false });
    }
  }, [isCountableUnit, headerQuantity, itemPath, getValues, setValue]);

  const { pendencies, isLocked } = useItemPricePendencies(itemPath);

  useEspecificacaoAutoFill({ itemPath, formats, finishings });

  const showWeightInGrid = !isServiceType && !isKgUnit && !weightOwnedByDimensions;
  const weightCell = (
    <FormGroup>
      <Label htmlFor={`${itemPath}.weight`} title="Usado na OP e na baixa de estoque">Peso total (kg)</Label>
      <Input
        id={`${itemPath}.weight`}
        autoComplete="off"
        type="number"
        step="any"
        title="Usado na OP e na baixa de estoque"
        {...register(`${itemPath}.weight` as any, { valueAsNumber: true })}
        placeholder="Digite"
        hasError={!!get(errors, `${itemPath}.weight`)}
      />
    </FormGroup>
  );

  const acabamentoCell = (
    <FormGroup>
      <Label htmlFor={`${itemPath}.finishingId`}>Acabamento</Label>
      <Controller
        name={`${itemPath}.finishingId` as any}
        control={control}
        render={({ field }) => (
          <Select
            id={`${itemPath}.finishingId`}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
            hasError={!!get(errors, `${itemPath}.finishingId`)}
          >
            <option value="">Selecione</option>
            {finishings.map(finishing => (
              <option key={finishing.id} value={finishing.id}>{finishing.name}</option>
            ))}
          </Select>
        )}
      />
    </FormGroup>
  );

  return (
    <>
      <FormGrid columns={3}>
        {typeSelectSlot || <FormGroup />}
        <WideFormGroup>
          <Label htmlFor={`${itemPath}.nomeCustomizado`}>Especificação</Label>
          <Input
            id={`${itemPath}.nomeCustomizado`}
            autoComplete="off"
            {...register(`${itemPath}.nomeCustomizado` as any)}
            hasError={!!get(errors, `${itemPath}.nomeCustomizado`)}
          />
        </WideFormGroup>
      </FormGrid>

      {identificationSlot}

      {dimensionsSlot}

      <SpacedFormGrid columns={3}>
        <FormGroup>
          <Label htmlFor={`${itemPath}.unitOfMeasurementId`}>Unidade</Label>
          <Controller
            name={`${itemPath}.unitOfMeasurementId` as any}
            control={control}
            render={({ field }) => (
              <Select
                id={`${itemPath}.unitOfMeasurementId`}
                value={field.value ?? ''}
                onChange={(e) => handleUnitChange(e.target.value, field.onChange)}
                disabled={!!unitFromProduct}
                hasError={!!get(errors, `${itemPath}.unitOfMeasurementId`)}
              >
                <option value="">Selecione</option>
                {commercialUnits.map(unit => (
                  <option key={unit.id} value={unit.id}>{unit.name}</option>
                ))}
              </Select>
            )}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor={`${itemPath}.quantity`}>
            Quantidade{selectedUnitName ? ` (${selectedUnitName})` : ''}
          </Label>
          <Input
            id={`${itemPath}.quantity`}
            autoComplete="off"
            type="number"
            step="any"
            {...register(`${itemPath}.quantity` as any, { valueAsNumber: true })}
            placeholder="Total na unidade"
            hasError={!!get(errors, `${itemPath}.quantity`) || stockExceeded}
          />
          {stockExceeded && (
            <StockWarning>
              ⚠ Estoque insuficiente: disponível {Number(stockAvailable.quantity).toLocaleString('pt-BR')}
              {stockAvailable.unit ? ` ${stockAvailable.unit}` : ''} — o pedido não poderá ser salvo acima disso.
            </StockWarning>
          )}
        </FormGroup>
        {!weightOwnedByDimensions ? (
          <FormGroup>
            <Label htmlFor={`${itemPath}.quantityPieces`}>Quantidade de peças</Label>
            <Input
              id={`${itemPath}.quantityPieces`}
              autoComplete="off"
              type="number"
              step="1"
              min="0"
              {...register(`${itemPath}.quantityPieces` as any, { valueAsNumber: true })}
              placeholder="Digite"
              hasError={!!get(errors, `${itemPath}.quantityPieces`)}
            />
          </FormGroup>
        ) : (
          <FormGroup />
        )}
      </SpacedFormGrid>

      {detailsSlot}

      <CenteredFormGrid columns={3}>
        {!isServiceType && !isProductType && (
          <>
            <FormGroup>
              <Label htmlFor={`${itemPath}.formatId`}>Formato</Label>
              <Controller
                name={`${itemPath}.formatId` as any}
                control={control}
                render={({ field }) => (
                  <Select
                    id={`${itemPath}.formatId`}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    hasError={!!get(errors, `${itemPath}.formatId`)}
                  >
                    <option value="">Selecione</option>
                    {formats.map(format => (
                      <option key={format.id} value={format.id}>{format.name}</option>
                    ))}
                  </Select>
                )}
              />
            </FormGroup>
            {acabamentoCell}
            {showWeightInGrid ? weightCell : <FormGroup />}
          </>
        )}
        {isProductType && showWeightInGrid && (
          <>
            {weightCell}
            <FormGroup />
            <FormGroup />
          </>
        )}
        <FormGroup>
          <Label htmlFor={`${itemPath}.additionalData`}>Dados adicionais</Label>
          <Textarea
            id={`${itemPath}.additionalData`}
            {...register(`${itemPath}.additionalData` as any)}
            placeholder="Digite aqui"
            hasError={!!get(errors, `${itemPath}.additionalData`)}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor={`${itemPath}.extraInfo`}>Observação</Label>
          <Textarea
            id={`${itemPath}.extraInfo`}
            {...register(`${itemPath}.extraInfo` as any)}
            placeholder="Digite aqui"
            hasError={!!get(errors, `${itemPath}.extraInfo`)}
          />
        </FormGroup>
        {isTopLevelItem ? (
          <FormGroup>
            <Label htmlFor={`${itemPath}.productionObservation`}>Observações para produção</Label>
            <Textarea
              id={`${itemPath}.productionObservation`}
              {...register(`${itemPath}.productionObservation` as any)}
              hasError={!!get(errors, `${itemPath}.productionObservation`)}
            />
          </FormGroup>
        ) : (
          <FormGroup />
        )}
        {isProductType && attachmentSlot}
        {isLocked && (
          <PriceLockBox>
            <PriceLockText>
              ⚠ Preço travado — {pendencies.length} {pendencies.length === 1 ? 'pendência' : 'pendências'}
            </PriceLockText>
            <HintText>Resolva nos avisos da composição abaixo.</HintText>
          </PriceLockBox>
        )}
      </CenteredFormGrid>

      <ConfirmationModal
        isOpen={showClearUnitConfirm}
        title="Limpar a unidade do item?"
        message="A composição depende da unidade — matéria-prima, serviços, insumos e preços deste item serão resetados. Deseja continuar?"
        actions={[
          { label: 'Não, manter', onClick: () => setShowClearUnitConfirm(false), variant: 'outline' },
          { label: 'Sim, limpar e resetar', onClick: confirmClearUnit, variant: 'danger' },
        ]}
      />
    </>
  );
};

export default ItemGeneralFieldsSection;
